/* eslint-disable indent */
import queryString from 'query-string';
import { convertPascalCaseToKebabCase } from 'utils/string';
import { type FilterItem, type ActiveFilter, type Filter } from './ListingFiltersTypes';

// get field and return as string[]
export const getActiveFilterValueByField = (activeFilters: ActiveFilter[], fieldName: string): string[] => {
    return activeFilters.find(({ field }) => {
        return field === fieldName;
    })?.values || [];
};

// get field and return as string from filters[0]
export const getActiveFilterSingleValue = (activeFilters: ActiveFilter[], fieldName: string): string => {
    return activeFilters.find(({ field }) => {
        return field === fieldName;
    })?.values[0] || '';
};

// get the id value based on the value and field
export const getIdValue = (filterSetting: Filter[], value: string, fieldName: string): number | null => {
    const filter = filterSetting.find(({ field }) => {
        return field === fieldName;
    });

    if (!filter) {
        return null;
    }

    return filter.items?.find(filterItem => {
        return filterItem.value === value;
    })?.id || null;
};


// clean filters by set empty array on all filters
export const getCleanFilters = (filters: Filter[]): ActiveFilter[] => {
    return filters.map(({ field, type }) => {
        return {
            field,
            type,
            values: [],
        };
    });
};

// replace related filter based on field with the new value
export const getUpdatedFilters = (
    activeFilters: ActiveFilter[],
    field: string,
    value: ActiveFilter['values']
): ActiveFilter[] => {
    return activeFilters.map(activeFilter => {
        return activeFilter.field === field ? {
            field,
            type: activeFilter.type,
            values: value,
        } : activeFilter;
    });
};

interface FilterQuery {
    [key: string]: string[];
}

// convert active filters to query needed for graphql
export const getFilterQuery = (activeFilters: ActiveFilter[], filterSetting?: Filter[]): FilterQuery => {
    if (!filterSetting) {
        return {};
    }

    // convert array [{ field, filters }] into object { [field]: filters, [field]: filters }
    return filterSetting.reduce((prevValue, currentValue) => {
        return ({ ...prevValue, [currentValue.field]: getActiveFilterValueByField(activeFilters, currentValue.field) });
    }, {});
};

// interface EmptyFilters {
//     [key: string]: string[] | undefined;
// }

// export const getEmptyValueFilters = (filterSetting: Filter[], emptyValue?: string[]): EmptyFilters => {
//     return filterSetting.reduce((prevValue, currentValue) => {
//         return ({ ...prevValue, [currentValue.field]: emptyValue });
//     }, {});
// };

// convert active filters to url parameters and return with current url
export const getQueryURL = (activeFilters: ActiveFilter[], filterSetting?: Filter[], restQuery = {}): string => {
    if (!filterSetting) {
        return '';
    }

    // convert array [{ field, filters }] into object { [field]: filters, [field]: filters }
    // and update value into kebabcase
    const query = activeFilters.reduce((prevActiveFilter, currentActiveFilter) => {
        return ({
            ...prevActiveFilter, [convertPascalCaseToKebabCase(currentActiveFilter.field)]: currentActiveFilter.values.map(value => {
                switch (currentActiveFilter.type) {
                    case 'input':
                    case 'year-select':
                        // for something that has no id
                        return value ? convertPascalCaseToKebabCase(value) : null;
                    default: {
                        const id = getIdValue(filterSetting, value, currentActiveFilter.field);
                        return id ? convertPascalCaseToKebabCase(String(id)) : undefined;
                    }
                }
            }),
        });
    }, {});

    // added with rest query (additional if needed)
    return queryString.stringifyUrl({ url: window.location.href, query: { ...query, ...restQuery } });
};

// convert url params to active filters
export const getActiveFiltersFromURL = (filterSetting?: Filter[]): ActiveFilter[] => {
    if (!filterSetting) {
        return [];
    }

    const parsedQueryUrl = queryString.parse(window.location.search);

    return filterSetting.map(({ field, type, items }) => {
        // get the query value from url for this field
        const queryValue = parsedQueryUrl[field];

        const DEFAULT_EMPTY_VALUE = {
            field,
            type,
            values: [],
        };

        // if not found just return empty array
        if (!queryValue) {
            return DEFAULT_EMPTY_VALUE;
        }

        // if string just make it string[], makes it easier to check, otherwise remove the null value
        const queryValueArray = (typeof queryValue === 'string') ?
            [queryValue] :
            queryValue.filter(value => {
                return value !== null;
            }) as string[];

        // if no items (search field)
        if (!items) {
            return {
                field,
                type,
                values: queryValueArray,
            };
        }

        // else it's a select or multiple choice
        const item = items.filter(({ id }) => {
            return queryValueArray.includes(convertPascalCaseToKebabCase(String(id)));
        });

        // if not found
        if (!item.length) {
            return DEFAULT_EMPTY_VALUE;
        }

        // else get the value
        return {
            field,
            type,
            values: item.map(({ value }) => {
                return value;
            }),
        };
    });
};

export const getPageQueryURL = (page: number): string => {
    return queryString.stringifyUrl({ url: window.location.href, query: { page: !page ? undefined : page + 1 } });
};

export const getPageFromURL = (): number => {
    const parsedQueryUrl = queryString.parse(window.location.search);
    const queryValue = parsedQueryUrl.page;
    return Number(queryValue) - 1 || 0;
};


export interface WhereItem {
    type: 'search' | 'year' | 'guid' | 'ancestors';
    field: string;
    value: string[];
}

// any here because it's too complex could be any object value
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getWhereValue = (whereItems: WhereItem[]): any => {
    if (!whereItems?.length) {
        return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const andValue: any = [];
    whereItems.forEach(({ value, type, field }) => {
        if (!value?.length) {
            return;
        }

        switch (type) {
            case 'year': {
                andValue.push({ _and: [{ [field]: { gte: `${value[0]}-1-1` } }, { [field]: { lt: `${Number(value[0]) + 1}-1-1` } }] });
                break;
            }
            case 'guid': {
                andValue.push({ ContentLink: { [field]: { in: value } } });
                break;
            }
            case 'ancestors': {
                andValue.push({ [field]: { in: value } });
                break;
            }
            default: { // for search
                // like vs contains
                // like = can use % to search just part of it
                // contains = can't search part of the word. i.e: search "test" can't with "tes"
                // const where = { _or: [{ _fulltext: { like: `%${e.target.value}%` } }] };
                andValue.push({ _fulltext: { like: `%${value[0].trim() || ''}%` } });
            }
        }
    });

    if (!andValue.length) {
        return undefined;
    }

    return { _and: andValue };
};


export const generateYearRangeItems = (startYear: number, endYear: number = new Date().getFullYear()): FilterItem[] => {
    const items: FilterItem[] = [];
    for (let i = startYear; i <= endYear; i += 1) {
        items.push({
            id: i,
            value: String(i),
            label: String(i),
        });
    }

    return items;
};
